import useComponentDidMount from "hooks/useComponentDidMount";
import { useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import useStateRef from "react-usestateref";
import GeneralContext from "./context";

type InvalidRoute = {
  name: string;
  error: string;
};

const GeneralProvider = ({ children }: { children: React.ReactNode }) => {
  const { pathname } = useLocation();

  const [, setIsFirstPageToRender, { current: isFirstPageToRender }] =
    useStateRef(true);

  useComponentDidMount(() => {
    setIsFirstPageToRender(false);
  }, [pathname]);

  const invalidRoutesRef = useRef({});
  const validRoutesRef = useRef<Record<string, boolean>>({});

  const addInvalidRoute = useCallback((route: InvalidRoute) => {
    invalidRoutesRef.current = Object.assign(invalidRoutesRef.current, {
      [route.name]: route.error,
    });
  }, []);

  const addValidRoute = useCallback((route: string) => {
    validRoutesRef.current = Object.assign(validRoutesRef.current, {
      [route]: true,
    });
  }, []);

  const removeInvalidRoute = useCallback((routeName: string) => {
    const updatedInvalids = { ...invalidRoutesRef.current };
    delete updatedInvalids[routeName as keyof typeof updatedInvalids];
    invalidRoutesRef.current = updatedInvalids;
  }, []);

  const value = {
    isFirstPageToRender,
    setIsFirstPageToRender,
    addInvalidRoute,
    invalidRoutes: invalidRoutesRef.current,
    validRoutes: validRoutesRef.current,
    addValidRoute,
    removeInvalidRoute,
  };

  return (
    <GeneralContext.Provider value={value} test-id="ws8c92ne">
      {children}
    </GeneralContext.Provider>
  );
};

export default GeneralProvider;
